import React, { createContext, useContext, useEffect, useState } from 'react';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// ✅ Cache TTL: 5 minutos para datos en memoria
const MEMORY_CACHE_TTL = 5 * 60 * 1000;
// ✅ localStorage TTL: 15 minutos para datos persistentes
const STORAGE_CACHE_TTL = 15 * 60 * 1000;
// ✅ Cache TTL para Board data: 30 segundos (datos muy dinámicos - pausar/despausar frecuente)
const BOARD_CACHE_TTL = 30 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface FirebaseCacheContextValue {
  // Miembros de familia (raramente cambian)
  getMembers: (familyId: string) => Promise<any[]>;
  invalidateMembers: (familyId: string) => void;
  
  // Configuración de familia
  getFamilyConfig: (familyId: string) => Promise<any | null>;
  invalidateFamilyConfig: (familyId: string) => void;
  
  // Activities de familia
  getActivities: (familyId: string, memberId?: string) => Promise<any[]>;
  invalidateActivities: (familyId: string) => void;
  
  // Checklists de familia
  getChecklists: (familyId: string, memberId?: string) => Promise<any[]>;
  invalidateChecklists: (familyId: string) => void;
  
  // Events de familia
  getEvents: (familyId: string) => Promise<any[]>;
  invalidateEvents: (familyId: string) => void;
  
  // Limpiar todo el caché
  clearAllCache: () => void;
}

const FirebaseCacheContext = createContext<FirebaseCacheContextValue | null>(null);

export function useFirebaseCache() {
  const context = useContext(FirebaseCacheContext);
  if (!context) {
    throw new Error('useFirebaseCache must be used within FirebaseCacheProvider');
  }
  return context;
}

// ✅ Caché en memoria (compartido entre componentes)
const memoryCache = new Map<string, CacheEntry<any>>();

// ✅ Helper para localStorage con manejo de errores
function getFromStorage(key: string): any | null {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    const cached: CacheEntry<any> = JSON.parse(item);
    if (Date.now() - cached.timestamp > STORAGE_CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }
    
    return cached.data;
  } catch {
    return null;
  }
}

function setToStorage(key: string, data: any): void {
  try {
    const cached: CacheEntry<any> = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cached));
  } catch {
    // Ignorar errores de quota
  }
}

export function FirebaseCacheProvider({ children }: { children: React.ReactNode }) {
  // ========== MEMBERS ==========
  const getMembers = async (familyId: string): Promise<any[]> => {
    const cacheKey = `members_${familyId}`;
    
    // 1. Revisar memoria
    const memCached = memoryCache.get(cacheKey);
    if (memCached && Date.now() - memCached.timestamp < MEMORY_CACHE_TTL) {
      return memCached.data;
    }
    
    // 2. Revisar localStorage
    const storageCached = getFromStorage(cacheKey);
    if (storageCached) {
      // Guardar en memoria también
      memoryCache.set(cacheKey, { data: storageCached, timestamp: Date.now() });
      return storageCached;
    }
    
    // 3. Fetch desde Firestore
    const q = query(
      collection(db, 'members'),
      where('familyId', '==', familyId),
      limit(50) // ✅ Máximo 50 miembros
    );
    
    const snapshot = await getDocs(q);
    const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // 4. Guardar en ambos cachés
    memoryCache.set(cacheKey, { data: members, timestamp: Date.now() });
    setToStorage(cacheKey, members);
    
    return members;
  };
  
  const invalidateMembers = (familyId: string) => {
    const cacheKey = `members_${familyId}`;
    memoryCache.delete(cacheKey);
    try {
      localStorage.removeItem(cacheKey);
    } catch {}
  };
  
  // ========== FAMILY CONFIG ==========
  const getFamilyConfig = async (familyId: string): Promise<any | null> => {
    const cacheKey = `family_${familyId}`;
    
    // 1. Revisar memoria
    const memCached = memoryCache.get(cacheKey);
    if (memCached && Date.now() - memCached.timestamp < MEMORY_CACHE_TTL) {
      return memCached.data;
    }
    
    // 2. Revisar localStorage
    const storageCached = getFromStorage(cacheKey);
    if (storageCached) {
      memoryCache.set(cacheKey, { data: storageCached, timestamp: Date.now() });
      return storageCached;
    }
    
    // 3. Fetch desde Firestore (por docId directo, evita problemas con __name__)
    const snap = await getDoc(doc(db, 'families', familyId));
    if (!snap.exists()) return null;

    const family = { id: snap.id, ...snap.data() };
    
    // 4. Guardar en ambos cachés
    memoryCache.set(cacheKey, { data: family, timestamp: Date.now() });
    setToStorage(cacheKey, family);
    
    return family;
  };
  
  const invalidateFamilyConfig = (familyId: string) => {
    const cacheKey = `family_${familyId}`;
    memoryCache.delete(cacheKey);
    try {
      localStorage.removeItem(cacheKey);
    } catch {}
  };
  
  // ========== ACTIVITIES ==========
  const getActivities = async (familyId: string, memberId?: string): Promise<any[]> => {
    const cacheKey = `activities_${familyId}${memberId ? `_${memberId}` : ''}`;
    
    // 1. Revisar memoria (TTL más corto para board data)
    const memCached = memoryCache.get(cacheKey);
    if (memCached && Date.now() - memCached.timestamp < BOARD_CACHE_TTL) {
      return memCached.data;
    }
    
    // 2. No usar localStorage para board data (cambia frecuentemente)
    
    // 3. Fetch desde Firestore
    // ✅ FIX: NO filtrar por active aquí, el Board lo filtra en memoria
    let q = query(
      collection(db, 'activities'),
      where('familyId', '==', familyId),
      limit(100)
    );
    
    // ✅ FASE 4B: Filtrar por member si se proporciona
    if (memberId) {
      q = query(
        collection(db, 'activities'),
        where('familyId', '==', familyId),
        where('assignedTo', 'array-contains', memberId),
        limit(50)
      );
    }
    
    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // 4. Guardar solo en memoria (no localStorage)
    memoryCache.set(cacheKey, { data: activities, timestamp: Date.now() });
    
    return activities;
  };
  
  const invalidateActivities = (familyId: string) => {
    // Limpiar todas las variantes (con y sin memberId)
    const keysToDelete = Array.from(memoryCache.keys()).filter(key => 
      key.startsWith(`activities_${familyId}`)
    );
    keysToDelete.forEach(key => memoryCache.delete(key));
  };
  
  // ========== CHECKLISTS ==========
  const getChecklists = async (familyId: string, memberId?: string): Promise<any[]> => {
    const cacheKey = `checklists_${familyId}${memberId ? `_${memberId}` : ''}`;
    
    // 1. Revisar memoria
    const memCached = memoryCache.get(cacheKey);
    if (memCached && Date.now() - memCached.timestamp < BOARD_CACHE_TTL) {
      return memCached.data;
    }
    
    // 2. Fetch desde Firestore
    // ✅ FIX: NO filtrar por active aquí, el Board lo filtra en memoria
    let q = query(
      collection(db, 'checklist'),
      where('familyId', '==', familyId),
      limit(100)
    );
    
    // ✅ FASE 4B: Filtrar por member si se proporciona
    if (memberId) {
      q = query(
        collection(db, 'checklist'),
        where('familyId', '==', familyId),
        where('assignedTo', 'array-contains', memberId),
        limit(50)
      );
    }
    
    const snapshot = await getDocs(q);
    const checklists = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // 3. Guardar solo en memoria
    memoryCache.set(cacheKey, { data: checklists, timestamp: Date.now() });
    
    return checklists;
  };
  
  const invalidateChecklists = (familyId: string) => {
    const keysToDelete = Array.from(memoryCache.keys()).filter(key => 
      key.startsWith(`checklists_${familyId}`)
    );
    keysToDelete.forEach(key => memoryCache.delete(key));
  };
  
  // ========== EVENTS ==========
  const getEvents = async (familyId: string): Promise<any[]> => {
    const cacheKey = `events_${familyId}`;
    
    // 1. Revisar memoria
    const memCached = memoryCache.get(cacheKey);
    if (memCached && Date.now() - memCached.timestamp < BOARD_CACHE_TTL) {
      return memCached.data;
    }
    
    // 2. Fetch desde Firestore (eventos próximos)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const q = query(
      collection(db, 'events'),
      where('familyId', '==', familyId),
      where('start', '>=', today),
      limit(50)
    );
    
    const snapshot = await getDocs(q);
    const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // 3. Guardar solo en memoria
    memoryCache.set(cacheKey, { data: events, timestamp: Date.now() });
    
    return events;
  };
  
  const invalidateEvents = (familyId: string) => {
    const cacheKey = `events_${familyId}`;
    memoryCache.delete(cacheKey);
  };
  
  // ========== CLEAR ALL ==========
  const clearAllCache = () => {
    memoryCache.clear();
    try {
      // Limpiar solo nuestras keys
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('members_') || key.startsWith('family_')) {
          localStorage.removeItem(key);
        }
      });
    } catch {}
  };
  
  const value: FirebaseCacheContextValue = {
    getMembers,
    invalidateMembers,
    getFamilyConfig,
    invalidateFamilyConfig,
    getActivities,
    invalidateActivities,
    getChecklists,
    invalidateChecklists,
    getEvents,
    invalidateEvents,
    clearAllCache
  };
  
  return (
    <FirebaseCacheContext.Provider value={value}>
      {children}
    </FirebaseCacheContext.Provider>
  );
}

// ✅ Hook conveniente para usar el caché de miembros
export function useCachedMembers(familyId: string | null) {
  const { getMembers } = useFirebaseCache();
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!familyId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    
    let isMounted = true;
    
    getMembers(familyId).then(data => {
      if (isMounted) {
        setMembers(data);
        setLoading(false);
      }
    }).catch(() => {
      if (isMounted) {
        setMembers([]);
        setLoading(false);
      }
    });
    
    return () => {
      isMounted = false;
    };
  }, [familyId, getMembers]);
  
  return { members, loading };
}
